import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { ProjectsFilterType } from "@/components/projects/ProjectConstants";
import { useRequireProject } from "@/hooks/use-require-project";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import { useMembers } from "@/queries/organization-queries";
import { useListProjects, useListProjectStates } from "@/queries/project-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import Fuse from "fuse.js";
import type { Project } from "gitpod-next-api/gitpod/v1/project_pb";
import { useMemo, type FC } from "react";
import { BlankEnvironmentCard, NoProjectCard, ProjectCard } from "./ProjectCard";
import { useFlag } from "@/hooks/use-flag";
import { Flags } from "@/feature-flags";
import { useListRunnerEnvironmentClasses } from "@/queries/runner-configuration-queries";

const searchOptions = {
    includeScore: true,
    keys: [
        "project.metadata.name",
        "project.id",
        {
            name: "contextUrl",
            getFn: (item: { project: PlainMessage<Project> }) =>
                getRepoUrlFromInitializer(item.project.initializer) ?? "",
        },
    ],
};

export const ProjectsList: FC<{
    canEdit: boolean;
    showRecentlyUsedOnly?: boolean;
    showNoProjectCard?: boolean;
    showBlankEnvironmentCard?: boolean;
    filter?: ProjectsFilterType;
    search?: string;
    selectProjectId?: string;
    selectEnvironmentClass?: string;
    selectRunnerId?: string;
    disableActions?: boolean;

    onCreateSuccess?: () => void;
    onCreateFromUrl?: (url?: string) => void;
    onCreateBlankEnvironment?: () => void;
}> = ({
    canEdit,
    showNoProjectCard,
    showBlankEnvironmentCard,
    filter,
    search,
    selectProjectId,
    selectEnvironmentClass,
    selectRunnerId,
    disableActions,
    onCreateSuccess,
    onCreateFromUrl,
    onCreateBlankEnvironment,
}) => {
    const { data: user, isLoading: isLoadingUser } = useAuthenticatedUser();
    const { data: projectData, isLoading: isLoadingProjects, isPending: isPendingProjects } = useListProjects();
    const { data: projectsStates, isLoading: isLoadingProjectsStates } = useListProjectStates(
        projectData?.projects.map((p) => p.id),
    );
    const { data: membersData, isPending: isPendingMembers } = useMembers();
    const { requireProjects, isPending: isPendingRequireProjects } = useRequireProject();

    const { value: allowCreationOfBlankEnvironments } = useFlag(Flags.AllowCreationOfBlankEnvironments, false);

    const allProjects = useMemo(() => {
        if (!membersData || !projectData || !projectsStates) {
            return []; // should be caught by isLoading check
        }

        return (projectData.projects ?? []).map((project, idx) => {
            return {
                project,
                state: projectsStates[idx],
            };
        });
    }, [membersData, projectData, projectsStates]);

    const { data: runnerEnvironmentClasses } = useListRunnerEnvironmentClasses(selectRunnerId);

    const filteredByState = useMemo(() => {
        return allProjects.filter((p) => {
            let visible = true;
            if (selectProjectId) {
                return p.project.id === selectProjectId;
            }
            if (selectEnvironmentClass) {
                if (selectEnvironmentClass === "local") {
                    return p.project.environmentClass?.environmentClass.case === "localRunner";
                } else {
                    return (
                        p.project.environmentClass?.environmentClass.case === "environmentClassId" &&
                        p.project.environmentClass.environmentClass.value === selectEnvironmentClass
                    );
                }
            }
            if (selectRunnerId) {
                return runnerEnvironmentClasses?.some(
                    (ec) => ec.id === p.project.environmentClass?.environmentClass?.value,
                );
            }
            if (filter === ProjectsFilterType.Active) {
                visible = p.state.shared;
            }
            if (filter === ProjectsFilterType.NotShared) {
                visible = !p.state.shared;
            }

            return visible;
        });
    }, [allProjects, filter, selectEnvironmentClass, selectProjectId, selectRunnerId, runnerEnvironmentClasses]);

    const projectsSearchIndex = useMemo(() => {
        return Fuse.createIndex(searchOptions.keys, filteredByState);
    }, [filteredByState]);

    const filteredBySearch = useMemo(() => {
        if (!search) {
            return filteredByState;
        }

        const fuse = new Fuse(filteredByState, searchOptions, projectsSearchIndex);
        const result = fuse.search(search);

        return result.map((r) => r.item);
    }, [filteredByState, projectsSearchIndex, search]);

    const isLoading =
        isLoadingProjects ||
        isLoadingProjectsStates ||
        isPendingProjects ||
        isPendingMembers ||
        isLoadingUser ||
        isPendingRequireProjects ||
        !membersData ||
        !projectData ||
        !projectsStates ||
        !user;

    if (isLoading) {
        return <LoadingState />;
    }

    const doShowNoProjectCard = !requireProjects && showNoProjectCard;
    const doShowBlankEnvironmentCard = allowCreationOfBlankEnvironments && showBlankEnvironmentCard;

    const noProjects = allProjects.length === 0;
    const noSearchResults = !noProjects && search && filteredBySearch.length === 0;

    return (
        <div className="flex flex-col gap-4 pb-6" data-testid="projects-list">
            {doShowNoProjectCard && <NoProjectCard onCreateEnvironment={() => onCreateFromUrl?.(search)} />}
            {doShowBlankEnvironmentCard && (
                <BlankEnvironmentCard onCreateEnvironment={() => onCreateBlankEnvironment?.()} />
            )}
            <div className="flex flex-col flex-nowrap gap-4">
                {filteredBySearch.map(({ project, state }) => (
                    <ProjectCard
                        key={project.id}
                        canEdit={canEdit}
                        project={project}
                        state={state}
                        orgMembers={membersData?.members ?? []}
                        currentUserId={user?.id ?? ""}
                        forceHoverState={!!selectProjectId}
                        onCreateSuccess={onCreateSuccess}
                        disableActions={disableActions}
                    />
                ))}
            </div>
            {noProjects && (
                <EmptyStateCard
                    title="No projects"
                    description="Projects are pre-configured templates that provide one-click environments. Only admins can create projects for your organization."
                    data-testid="no-projects-empty-state"
                />
            )}
            {noSearchResults && (
                <EmptyStateCard
                    title="No results found"
                    description="No projects match your search criteria."
                    data-testid="no-search-results-empty-state"
                />
            )}
        </div>
    );
};
